/**
 * Short, human labels for the twenty intents.
 *
 * Typed as `Record<Intent, string>` on purpose: adding an intent to the
 * vocabulary without giving it a label becomes a TYPE ERROR rather than a
 * screen that quietly renders `find_subscription_info` at a customer-support
 * agent. The descriptions in `@sophie/determinism` are full sentences meant for
 * a classifier prompt and are too long for a table cell — these are the same
 * vocabulary, said shorter.
 */

import type { Intent } from '@sophie/determinism';

const LABELS: Record<Intent, string> = {
  refund_request: 'Refund request',
  cancel_subscription: 'Cancel subscription',
  cancel_vitality_club: 'Cancel Vitality Club',
  cancel_order: 'Cancel order',
  change_frequency: 'Change frequency',
  change_quantity: 'Change quantity',
  skip_months: 'Skip shipments',
  reactivate_subscription: 'Reactivate',
  request_return: 'Return request',
  refund_return_status: 'Refund / return status',
  tracking: 'Tracking',
  wrong_damaged_missing: 'Wrong / damaged / missing',
  checkout_issues: 'Checkout issue',
  modify_order: 'Modify order',
  chargeback_dispute: 'Chargeback',
  find_order_info: 'Order lookup',
  find_subscription_info: 'Subscription lookup',
  vitality_club_info: 'Vitality Club info',
  escalate: 'Asked for a human',
  product_question: 'Product question',
  business_inquiry: 'Business inquiry',
  solicitation: 'Sales pitch',
  marketing_unsubscribe: 'Marketing unsubscribe',
  unsubscribe_privacy: 'Data privacy',
  no_action_needed: 'No action needed',
  other: 'Other',
};

/**
 * Indexed loosely, because the column is a free-text string in the database.
 * `unclear` is a dashboard topic, not an intent: an "Other" ticket whose
 * router could not commit (low confidence, empty message).
 */
export const INTENT_DESCRIPTIONS: Record<string, string | undefined> = {
  ...LABELS,
  unclear: 'Unclear message',
};

/** The topics as filter options, in the vocabulary's order. */
export const INTENT_OPTIONS: ReadonlyArray<{ value: Intent; label: string }> = (
  Object.entries(LABELS) as Array<[Intent, string]>
).map(([value, label]) => ({ value, label }));
